import { ErreurApi, type Client } from '../../api/client.js';
import type { FiltreDocuments } from './filtres.js';
import { ETATS_ECRANS } from './libelles.js';
import { examinerPiece, type DecisionExamen } from './revue.js';
import type { DocumentLakou, VersionDocument } from './types.js';
import { aller, dezoomer, etatInitial, pivoter, zoomer, type EtatVisionneuse } from './visionneuse.js';

/**
 * D09 · contrôleur de l'écran Documents : lit les pièces du dossier (`documents.lister`), ouvre une pièce (`documents.lire`),
 * enregistre la décision de l'équipe (`documents.examiner`). Chaque appel a son état de chargement et son message d'erreur ;
 * l'écran n'est jamais muet.
 */
export type StatutDocuments = 'sans_dossier' | 'chargement' | 'erreur' | 'pret';

export interface EtatDocuments {
  statut: StatutDocuments;
  /** Message de l'échec de lecture de la liste (phrase française du client de l'API). */
  message?: string | undefined;
  documents: DocumentLakou[];
  filtre: FiltreDocuments;
  selection: string | null;
  visionneuse: EtatVisionneuse;
  versions: VersionDocument[];
  /** Ouverture de la pièce choisie. */
  lecture: 'aucune' | 'chargement' | 'erreur' | 'prete';
  messageLecture?: string | undefined;
  /** Résultat de la dernière décision : enregistrée, ou refusée avec la phrase du client de l'API. */
  examen?: { etat: 'enregistre' | 'erreur'; message: string } | undefined;
}

export interface ControleurDocuments {
  etat(): EtatDocuments;
  abonner(ecouteur: () => void): () => void;
  charger(): Promise<void>;
  filtrer(filtre: FiltreDocuments): void;
  selectionner(documentId: string): Promise<void>;
  zoom(sens: 'plus' | 'moins'): void;
  pivoter(sens: 'horaire' | 'antihoraire'): void;
  page(n: number): void;
  examiner(decision: DecisionExamen, documentId: string, message?: string): Promise<void>;
}

const phrase = (erreur: unknown): string =>
  erreur instanceof ErreurApi ? erreur.messageUtilisateur : 'Une erreur est survenue de notre côté. Réessayez dans un instant.';

export function creerControleurDocuments({
  client,
  dossierId,
  decisionEnregistree,
  demandeEnregistree = ETATS_ECRANS.d09.demandeEnregistree,
}: {
  client: Client;
  dossierId: string | null;
  decisionEnregistree: string;
  /** Phrase après une demande de meilleure photo : aucun canal d'envoi n'est branché, elle ne doit jamais dire qu'un message est parti. */
  demandeEnregistree?: string;
}): ControleurDocuments {
  let courant: EtatDocuments = {
    statut: dossierId === null ? 'sans_dossier' : 'chargement',
    documents: [],
    filtre: {},
    selection: null,
    visionneuse: etatInitial(),
    versions: [],
    lecture: 'aucune',
  };
  const ecouteurs = new Set<() => void>();
  const poser = (modif: Partial<EtatDocuments>): void => {
    courant = { ...courant, ...modif };
    for (const e of [...ecouteurs]) e();
  };
  const remplacer = (doc: DocumentLakou): DocumentLakou[] => courant.documents.map((d) => (d.id === doc.id ? doc : d));
  const pagesDe = (id: string | null): number => courant.documents.find((d) => d.id === id)?.currentVersion.preview.pages ?? 1;

  return {
    etat: () => courant,
    abonner(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => ecouteurs.delete(ecouteur);
    },
    async charger() {
      if (dossierId === null) return;
      poser({ statut: 'chargement' });
      try {
        const documents = await client.appeler<DocumentLakou[]>('documents.lister', { parametres: { dossierId } });
        poser({ statut: 'pret', documents, message: undefined });
      } catch (erreur) {
        poser({ statut: 'erreur', message: phrase(erreur) });
      }
    },
    filtrer(filtre) {
      poser({ filtre });
    },
    async selectionner(documentId) {
      poser({ selection: documentId, visionneuse: etatInitial(), versions: [], lecture: 'chargement', examen: undefined });
      try {
        const doc = await client.appeler<DocumentLakou>('documents.lire', { parametres: { documentId } });
        if (courant.selection !== documentId) return;
        poser({ documents: remplacer(doc), versions: [doc.currentVersion], lecture: 'prete', messageLecture: undefined });
      } catch (erreur) {
        if (courant.selection !== documentId) return;
        poser({ lecture: 'erreur', messageLecture: phrase(erreur) });
      }
    },
    zoom(sens) {
      poser({ visionneuse: sens === 'plus' ? zoomer(courant.visionneuse) : dezoomer(courant.visionneuse) });
    },
    pivoter(sens) {
      poser({ visionneuse: pivoter(courant.visionneuse, sens) });
    },
    page(n) {
      poser({ visionneuse: aller(courant.visionneuse, n, pagesDe(courant.selection)) });
    },
    async examiner(decision, documentId, message) {
      try {
        const doc = await examinerPiece(client, documentId, decision, message);
        poser({ documents: remplacer(doc), examen: { etat: 'enregistre', message: decision === 'meilleure_photo' ? demandeEnregistree : decisionEnregistree } });
      } catch (erreur) {
        poser({ examen: { etat: 'erreur', message: phrase(erreur) } });
      }
    },
  };
}
